import { useTrans } from "../hooks";
import {
    VStack,
    Text,
    Box,
    HStack,
    Flex,
    Checkbox,
    Input,
} from "@chakra-ui/react";
import { TimeSlots } from "../apis";
import {
    Control,
    Controller,
    FieldValues,
    UseFormRegister,
} from "react-hook-form";
import { AppSelect, AppSelectOption } from "./AppSelect";
import { AppFormControl } from "./AppFormControl";
import { AppDatePicker } from "./AppDatePicker";
import { APPC, UTIL } from "../config";

export interface InternshipWeekScheduleProps<
    T extends FieldValues = FieldValues,
> {
    timeSlots: TimeSlots;
    register: UseFormRegister<T>;
    control: Control<T>;
    loading: boolean;
}

export const AppInternshipWeekSchedule = <T extends FieldValues>({
    timeSlots,
    register,
    control,
    loading,
}: InternshipWeekScheduleProps<T>) => {
    const { t } = useTrans();
    const dayjs = UTIL.getDayJs();
    return (
        <>
            {timeSlots.map((week, index) => (
                <VStack
                    key={index + 1}
                    bg={"var(--chakra-colors-greyT95)"}
                    borderRadius={8}
                    p={4}
                    flexWrap={"nowrap"}
                    flexShrink={0}
                    alignItems={"flex-start"}
                    gap={2}
                >
                    <Text
                        textStyle={"mediumMd"}
                        textDecoration={"underline"}
                        mb={2}
                    >
                        {`${index + 1} ${t("com.AppInternshipWeekSchedule:text.week")}`}
                    </Text>
                    {week?.length < 7 &&
                        index === 0 &&
                        week[0].day !== "mon" &&
                        [...Array(7 - week.length)].map((_, index2) => (
                            <Box key={index2} h="35.13px" />
                        ))}
                    {week.map((day, dayIndex) => {
                        const timeslotKey = `${dayjs(day.date).format(
                            APPC.APP_HTML_DATE_FORMAT
                        )}`;
                        return (
                            <HStack key={timeslotKey} w={"full"} gap={2}>
                                <input
                                    type="date"
                                    {...register(
                                        `timeSlots.${timeslotKey}.date` as any
                                    )}
                                    value={
                                        day.date
                                            ? dayjs(day.date).format(
                                                  APPC.APP_HTML_DATE_FORMAT
                                              )
                                            : ""
                                    }
                                    style={{ display: "none" }}
                                />
                                <input
                                    {...register(
                                        `timeSlots.${timeslotKey}.day` as any
                                    )}
                                    value={day.day}
                                    style={{ display: "none" }}
                                />
                                <Flex w={"120px"} alignSelf={"stretch"}>
                                    <AppFormControl
                                        label={
                                            !dayIndex
                                                ? t(
                                                      "ent.InternshipSchedule:days.label"
                                                  )
                                                : undefined
                                        }
                                        isLoading={loading}
                                    >
                                        <Checkbox
                                            mt={!dayIndex ? 4 : 2}
                                            {...register(
                                                `timeSlots.${timeslotKey}.isChecked` as any
                                            )}
                                            defaultChecked={day.isChecked}
                                        >
                                            <Text>
                                                {dayjs(day.date).format(
                                                    "ddd DD MMM"
                                                )}
                                            </Text>
                                        </Checkbox>
                                    </AppFormControl>
                                </Flex>
                                <Flex w={"100px"}>
                                    <AppFormControl
                                        label={
                                            !dayIndex
                                                ? t(
                                                      "ent.InternshipSchedule:startTime.label"
                                                  )
                                                : undefined
                                        }
                                        isLoading={loading}
                                    >
                                        <Controller
                                            control={control}
                                            name={
                                                `timeSlots.${timeslotKey}.startTime` as any
                                            }
                                            defaultValue={
                                                typeof day.startTime ===
                                                "string"
                                                    ? UTIL.toFixedUtcTime(
                                                          day.startTime
                                                      )
                                                    : (null as any)
                                            }
                                            render={({ field }) => (
                                                <AppDatePicker
                                                    selected={
                                                        UTIL.isValidDate(
                                                            field.value
                                                        )
                                                            ? field.value
                                                            : null
                                                    }
                                                    onChange={(date) =>
                                                        field.onChange(date)
                                                    }
                                                    isClearable
                                                    placeholderText={t(
                                                        "ent.InternshipSchedule:startTime.placeholder"
                                                    )}
                                                    showTimeSelect
                                                    showTimeSelectOnly
                                                    timeIntervals={60}
                                                    timeCaption={t(
                                                        "cmn:label.time"
                                                    )}
                                                    dateFormat={
                                                        APPC.APP_DP_TIME_FORMAT
                                                    }
                                                    timeFormat={
                                                        APPC.APP_DP_TIME_FORMAT
                                                    }
                                                    customInput={
                                                        <Input size={"sm"} />
                                                    }
                                                />
                                            )}
                                        />
                                    </AppFormControl>
                                </Flex>
                                <Flex w={"100px"}>
                                    <AppFormControl
                                        label={
                                            !dayIndex
                                                ? t(
                                                      "ent.InternshipSchedule:endTime.label"
                                                  )
                                                : undefined
                                        }
                                        isLoading={loading}
                                    >
                                        <Controller
                                            control={control}
                                            name={
                                                `timeSlots.${timeslotKey}.endTime` as any
                                            }
                                            defaultValue={
                                                typeof day.endTime === "string"
                                                    ? UTIL.toFixedUtcTime(
                                                          day.endTime
                                                      )
                                                    : (null as any)
                                            }
                                            render={({ field }) => (
                                                <AppDatePicker
                                                    selected={
                                                        UTIL.isValidDate(
                                                            field.value
                                                        )
                                                            ? field.value
                                                            : null
                                                    }
                                                    onChange={(date) =>
                                                        field.onChange(date)
                                                    }
                                                    isClearable
                                                    placeholderText={t(
                                                        "ent.InternshipSchedule:endTime.placeholder"
                                                    )}
                                                    showTimeSelect
                                                    showTimeSelectOnly
                                                    timeIntervals={60}
                                                    timeCaption={t(
                                                        "cmn:label.time"
                                                    )}
                                                    dateFormat={
                                                        APPC.APP_DP_TIME_FORMAT
                                                    }
                                                    timeFormat={
                                                        APPC.APP_DP_TIME_FORMAT
                                                    }
                                                    customInput={
                                                        <Input size={"sm"} />
                                                    }
                                                />
                                            )}
                                        />
                                    </AppFormControl>
                                </Flex>
                                <Flex w={"100px"}>
                                    <AppFormControl
                                        label={
                                            !dayIndex
                                                ? t(
                                                      "ent.InternshipSchedule:breakDuration.label"
                                                  )
                                                : undefined
                                        }
                                        isLoading={loading}
                                    >
                                        <Controller
                                            control={control}
                                            name={
                                                `timeSlots.${timeslotKey}.breakDuration` as any
                                            }
                                            defaultValue={
                                                day.breakDuration as any
                                            }
                                            render={({ field }) => (
                                                <AppSelect<AppSelectOption>
                                                    name={field.name}
                                                    isLoading={loading}
                                                    options={APPC.FE.InternshipSchedule.BREAK_DURATION.map(
                                                        (br) => ({
                                                            value: br,
                                                            label: t(
                                                                `con.InternshipSchedule:${br}`
                                                            ),
                                                        })
                                                    )}
                                                    value={{
                                                        value: field.value,
                                                        label: t(
                                                            `con.InternshipSchedule:${field.value}`
                                                        ),
                                                    }}
                                                    placeholder={t(
                                                        "ent.InternshipSchedule:breakDuration.placeholder"
                                                    )}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e?.value || 0
                                                        )
                                                    }
                                                    styles={{
                                                        control: (
                                                            baseStyles,
                                                            state
                                                        ) => ({
                                                            ...baseStyles,
                                                            borderColor:
                                                                state.isFocused
                                                                    ? "var(--chakra-colors-primary)"
                                                                    : "var(--chakra-colors-greyT65)",
                                                            minHeight: "32px",
                                                            ":hover": {
                                                                borderColor:
                                                                    state.isFocused
                                                                        ? "var(--chakra-colors-primary)"
                                                                        : "var(--chakra-colors-greyT40)",
                                                            },
                                                        }),
                                                        placeholder: (
                                                            baseStyles
                                                        ) => ({
                                                            ...baseStyles,
                                                            fontSize: 12,
                                                            color: "var(--chakra-colors-greyT70)",
                                                            paddingLeft: 4,
                                                        }),
                                                        singleValue: (
                                                            baseStyles
                                                        ) => ({
                                                            ...baseStyles,
                                                            fontSize: 12,
                                                            margin: 0,
                                                        }),
                                                        dropdownIndicator: (
                                                            baseStyles
                                                        ) => ({
                                                            ...baseStyles,
                                                            color: "var(--chakra-colors-primary)",
                                                            ":hover": {
                                                                color: "var(--chakra-colors-primary)",
                                                            },
                                                            svg: {
                                                                height: "14px",
                                                                width: "14px",
                                                                strokeWidth:
                                                                    "0.1",
                                                            },
                                                        }),
                                                    }}
                                                />
                                            )}
                                        />
                                    </AppFormControl>
                                </Flex>
                            </HStack>
                        );
                    })}
                    {week?.length < 7 &&
                        index + 1 === timeSlots.length &&
                        week[0].day === "mon" &&
                        [...Array(7 - week.length)].map((_, index2) => (
                            <Box key={index2} h="35.13px" />
                        ))}
                </VStack>
            ))}
        </>
    );
};
